// src/screens/FreedomNumberReveal.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 46A Section 7 — Merged "Today vs Target" + "Freedom Number" screen.
// Single screen: hero reveal, two-column comparison, expandable calculation.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { calcFreedomNumber } from '../utils/freedomNumberCalc';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

export default function FreedomNumberReveal({ data, onUpdate }: Props) {
  const result = calcFreedomNumber(data);

  // Sync calculated values back to ClientData
  const syncCalcs = useCallback(() => {
    if (result) {
      onUpdate({
        freedomNumberToday: Math.round(result.freedomNumberToday),
        freedomNumberInflated: Math.round(result.freedomNumberInflated),
        annualRetirementIncomeNeeded: Math.round(result.annualFromPot),
        gapAmount: Math.round(result.freedomNumberInflated - (data.voyantDoNothing.potAtRetirement || 0)),
      });
    }
  }, [result, data.voyantDoNothing.potAtRetirement, onUpdate]);

  useEffect(() => {
    syncCalcs();
  }, [syncCalcs]);

  // RLSA integration
  const rlsaTarget = data.rlsaAnnualTarget || result.weightedAnnual;
  const statePensionOffset = data.statePensionAnnual || 11502;
  const netAnnualGap = Math.max(0, rlsaTarget - statePensionOffset);

  const [showCalc, setShowCalc] = useState(false);

  // ── Today vs Target projections ───────────────────────────────────────────
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(1, freedomAge - clientAge);

  const projections = useMemo(() => {
    const GROWTH = data.assumptionPensionGrowth || 0.08;
    const ISA_GROWTH = data.assumptionIsaGrowth || 0.08;
    const CASH_GROWTH = data.assumptionCashRate || 0.01;

    const pensionBalance = data.pensionBalance || 0;
    const pensionMonthly = (data.pensionCompanyFunded || 0) + (data.pensionMonthly || 0);
    const pensionProjected =
      pensionBalance * Math.pow(1 + GROWTH, yearsToFreedom) +
      pensionMonthly * 12 * ((Math.pow(1 + GROWTH, yearsToFreedom) - 1) / GROWTH);

    const isaBalance = data.investmentsBalance || 0;
    const isaMonthly = data.isaContribution || 0;
    const isaProjected =
      isaBalance * Math.pow(1 + ISA_GROWTH, yearsToFreedom) +
      isaMonthly * 12 * ((Math.pow(1 + ISA_GROWTH, yearsToFreedom) - 1) / ISA_GROWTH);

    const cashBalance = data.efAccount1Balance || 0;
    const cashProjected = cashBalance * Math.pow(1 + CASH_GROWTH, yearsToFreedom);

    const total = pensionProjected + isaProjected + cashProjected;
    return { pensionProjected, isaProjected, cashProjected, total };
  }, [data, yearsToFreedom]);

  const freedomNumber = result.freedomNumberInflated;
  const rentalIncomeAnnual = (data.rentalIncomeNet ?? 0) * 12;

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* ── 1. Hero block ──────────────────────────────────────────────────── */}
      <div className="bg-ma-navy rounded-2xl p-10 text-white text-center mb-8">
        <p className="text-white/60 text-lg mb-6">
          {data.clientName || 'Your client'},
        </p>
        <p className="text-white/70 text-sm uppercase tracking-widest mb-4">
          Your Freedom Number is
        </p>
        <div className="text-6xl font-bold text-ma-gold mb-4">
          {fmt(Math.round(freedomNumber))}
        </div>
        <p className="text-white/50 text-sm">
          in today's money, growing with inflation
        </p>
      </div>

      {/* ── 2. Two-column comparison ───────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
        {/* Left — Today's Projection */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#F9FAFB',
            borderRadius: 12,
            padding: 28,
            border: '1px solid #E5E7EB',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#6B7280',
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            Today&rsquo;s Projection
          </div>
          <div style={{ fontSize: 15, color: '#374151', marginBottom: 20, lineHeight: 1.5 }}>
            Your pots at age {freedomAge} without any changes today
          </div>

          {[
            { label: 'Pension', value: projections.pensionProjected },
            { label: 'ISA', value: projections.isaProjected },
            { label: 'Cash', value: projections.cashProjected },
          ].map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: '1px solid #E5E7EB',
                fontSize: 15,
              }}
            >
              <span style={{ color: '#6B7280' }}>{row.label}</span>
              <span style={{ color: '#0B1A3E', fontWeight: 500 }}>{fmt(Math.round(row.value))}</span>
            </div>
          ))}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '14px 0 0',
              fontSize: 22,
            }}
          >
            <span style={{ color: '#0B1A3E', fontWeight: 700 }}>Total</span>
            <span style={{ color: '#0B1A3E', fontWeight: 700 }}>{fmt(Math.round(projections.total))}</span>
          </div>
          <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, fontStyle: 'italic' }}>
            Property equity not included — your home is not a drawdown asset.
          </p>
        </div>

        {/* Right — Freedom Number */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#0B1A3E',
            borderRadius: 12,
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#64DAD2',
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            Your Freedom Number
          </div>
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', marginBottom: 24, textAlign: 'center' }}>
            What you need at freedom age
          </div>
          <div
            style={{
              fontSize: 48,
              fontWeight: 700,
              color: '#64DAD2',
              marginBottom: 12,
            }}
          >
            {fmt(Math.round(freedomNumber))}
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            In today&rsquo;s money, growing with inflation
          </div>
        </div>
      </div>

      {/* Bridge panel */}
      <div
        style={{
          backgroundColor: '#FFF3CD',
          borderRadius: 8,
          padding: '20px 28px',
          textAlign: 'center',
          marginBottom: 32,
        }}
      >
        <div style={{ fontSize: 16, color: '#0B1A3E', fontWeight: 500, marginBottom: 4 }}>
          The gap between these two numbers is what today&rsquo;s session solves.
        </div>
        <div style={{ fontSize: 16, color: '#0B1A3E' }}>
          We&rsquo;re going to close it — decision by decision.
        </div>
      </div>

      {/* ── 3. Expandable "How we calculate this" ──────────────────────────── */}
      <div className="mb-8">
        <button
          type="button"
          onClick={() => setShowCalc(!showCalc)}
          className="w-full text-left bg-white rounded-2xl border border-gray-200 px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-semibold text-ma-navy">How we calculate this</span>
          <span className="text-gray-400 text-lg">{showCalc ? '▾' : '▸'}</span>
        </button>

        {showCalc && (
          <div className="bg-white border border-t-0 border-gray-200 rounded-b-2xl p-6 -mt-2 space-y-4">
            <p className="text-sm text-gray-600 leading-relaxed">
              Your Freedom Number is the lump sum needed at retirement to fund your chosen standard of living for life — using the 4% rule (Trinity Study: a diversified portfolio can sustain 4% annual withdrawals over 30+ years without running out).
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              We calculate in three spending phases — Comfortable to age 75, Moderate to 85, Minimum beyond — adjusted for inflation at 2.5% per year.
            </p>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Phase 1: Comfortable (age {data.targetFreedomAge}–75, {result.p1Years}yr)</span>
                <span className="font-medium">{fmt(result.p1Annual)}/year</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Phase 2: Moderate (age 75–85, {result.p2Years}yr)</span>
                <span className="font-medium">{fmt(result.p2Annual)}/year</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Phase 3: Minimum (age 85+, {result.p3Years}yr)</span>
                <span className="font-medium">{fmt(result.p3Annual)}/year</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-gray-700 font-medium">Blended average annual income</span>
                <span className="font-bold">{fmt(Math.round(result.weightedAnnual))}/year</span>
              </div>

              {result.mortgagePV > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">+ Mortgage payments — {result.mortgageYearsPastFreedom} years post-freedom (PV)</span>
                  <span className="font-medium">+{fmt(Math.round(result.mortgagePV))}</span>
                </div>
              )}
              {result.statePensionOffset > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">− State pension offset</span>
                  <span className="font-medium text-green-600">−{fmt(result.statePensionOffset)}</span>
                </div>
              )}
              {result.dbPensionOffset > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">− DB pension offset</span>
                  <span className="font-medium text-green-600">−{fmt(result.dbPensionOffset)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-gray-700 font-medium">Annual income from pot</span>
                <span className="font-bold">{fmt(Math.round(result.annualFromPot))}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>÷ 4% sustainable withdrawal</span>
                <span>= {fmt(Math.round(result.annualFromPot / 0.04))}</span>
              </div>
              {result.mortgagePV > 0 && (
                <div className="flex justify-between text-xs text-gray-400">
                  <span>+ Mortgage PV ({result.mortgageYearsPastFreedom} years)</span>
                  <span>= {fmt(result.freedomNumberToday)} (today&apos;s money)</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-400">
                <span>× 2.5% inflation for {result.yearsToFreedom} years</span>
                <span>= {fmt(result.freedomNumberInflated)}</span>
              </div>
            </div>

            {/* State pension note */}
            <p className="text-sm text-gray-600 leading-relaxed">
              <strong>State pension offset:</strong> From age {data.statePensionAge || 67} (your state pension age), your required drawdown reduces by approximately {fmt(data.statePensionAnnual || 0)}/year. This is already factored into your Freedom Number.
            </p>

            {/* Rental income note — conditional */}
            {rentalIncomeAnnual > 0 && (
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Your income sources in retirement:</strong> This plan assumes your primary income comes from your investment pot. Your rental income of {fmt(rentalIncomeAnnual)}/year is treated as additional retirement income and further reduces your required drawdown.
              </p>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">You want:</span>
                <span className="font-bold text-ma-navy">{fmt(Math.round(rlsaTarget))}/year in retirement</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">State pension:</span>
                <span className="font-bold text-green-600">-{fmt(Math.round(statePensionOffset))}/year</span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                <span className="text-gray-600">You need to fund:</span>
                <span className="font-bold text-ma-navy">{fmt(Math.round(netAnnualGap))}/year from your own pot</span>
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-2">
              Note: This is a planning approximation. Voyant gives the definitive figure at the end of Session 3.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
